import React, {useEffect, useState} from "react"
import {Alert, Button, Drawer, InputNumber, Switch} from "antd"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"
import {EncheresAPI, formatMontant} from "./api"

// La feuille de mise : ce qui s'ouvre quand on touche « Enchérir ».
//
// Deux temps, jamais un seul. D'abord le montant — trois paliers en gros
// boutons, parce qu'on enchérit debout, d'une main, et que taper un nombre est
// la dernière chose qu'on veut faire. Ensuite la confirmation, qui écrit
// l'engagement en toutes lettres : « Vous vous engagez à donner 360 € si vous
// l'emportez. » Une enchère ne se retire pas ; elle mérite cette seconde.
export default function BidSheet({ouvert, lot, vente, lang, identifier, onFerme, onMise}) {
    const [montant, setMontant] = useState(null)
    const [plafond, setPlafond] = useState(null)
    const [avecPlafond, setAvecPlafond] = useState(false)
    const [discret, setDiscret] = useState(false)
    const [etape, setEtape] = useState("montant")
    const [erreur, setErreur] = useState(null)
    const [envoi, setEnvoi] = useState(false)

    // La clé anti-doublon protège UNE tentative : un double appui sur
    // « Confirmer », ou un réseau qui rejoue la requête, ne pose qu'une enchère.
    //
    // ⚠️ Elle est retirée après un refus. Le serveur mémorise les refus sous
    // cette clé pour pouvoir répondre la même chose à une requête rejouée :
    // garder la clé, c'était rejouer éternellement « il faut au moins 260 € »,
    // même après être monté à 360 € — le fidèle restait bloqué jusqu'à fermer
    // et rouvrir la feuille, à la dernière minute avant Chabbat.
    const [cle, setCle] = useState(null)

    const tirerCle = () => (typeof window !== "undefined" && window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`

    useEffect(() => {
        if (!ouvert || !lot) return
        setMontant(lot.minimum || lot.startPrice || null)
        setPlafond(null)
        setAvecPlafond(false)
        setDiscret(false)
        setEtape("montant")
        setErreur(null)
        setCle(tirerCle())
    }, [ouvert, lot && lot.id])

    if (!lot) return null

    const devise = (vente && vente.devise) || "eur"
    const paliers = lot.paliers || []

    const confirmer = async () => {
        setEnvoi(true)
        setErreur(null)
        try {
            const res = await EncheresAPI.miser(identifier, lot.id, {
                montant,
                plafond: avecPlafond ? plafond : null,
                discret,
                cle,
            })
            if (!res || res.success === false) {
                setErreur(messageErreur(res, devise, lang))
                setEtape("montant")
                setCle(tirerCle())   // la tentative suivante est une NOUVELLE enchère
                setEnvoi(false)
                return
            }
            if (onMise) onMise(res)
            if (onFerme) onFerme()
        } catch (_) {
            // Une panne de réseau ne dit PAS si l'enchère est passée : on garde
            // la même clé, pour que le « Réessayez » ne puisse pas en poser deux.
            setErreur(t`Votre enchère n'est pas passée. Réessayez.`)
            setEtape("montant")
        } finally {
            setEnvoi(false)
        }
    }

    return (
        <Drawer
            open={ouvert}
            placement="bottom"
            height="auto"
            onClose={onFerme}
            title={lot.label}
            className="ench-sheet"
        >
            {erreur && <Alert type="warning" showIcon message={erreur} style={{marginBottom: 14}}/>}

            {etape === "montant" ? (
                <>
                    <div className="ench-sheet-actuel">
                        {lot.currentAmount
                            ? <Trans>Offre en cours : {formatMontant(lot.currentAmount, devise, lang)}</Trans>
                            : <Trans>Personne n'a encore enchéri.</Trans>}
                    </div>

                    <div className="ench-paliers">
                        {paliers.map(p => (
                            <button
                                key={p}
                                type="button"
                                className={"ench-palier" + (montant === p ? " ench-palier-actif" : "")}
                                onClick={() => setMontant(p)}
                            >
                                {formatMontant(p, devise, lang)}
                            </button>
                        ))}
                    </div>

                    <div className="ench-sheet-libre">
                        <span><Trans>Autre montant</Trans></span>
                        <InputNumber
                            min={lot.minimum || 0}
                            value={montant}
                            onChange={setMontant}
                            inputMode="numeric"
                            style={{width: 160}}
                        />
                    </div>

                    <div className="ench-sheet-option">
                        <div>
                            <div className="ench-sheet-option-titre"><Trans>Monter automatiquement pour moi</Trans></div>
                            <div className="ench-sheet-option-aide">
                                <Trans>
                                    Indiquez le maximum que vous acceptez de donner : si quelqu'un vous dépasse,
                                    le système enchérit pour vous, du strict nécessaire. Votre maximum n'est montré à personne.
                                </Trans>
                            </div>
                        </div>
                        <Switch checked={avecPlafond} onChange={setAvecPlafond}/>
                    </div>

                    {avecPlafond && (
                        <div className="ench-sheet-libre">
                            <span><Trans>Jusqu'à</Trans></span>
                            <InputNumber min={montant || 0} value={plafond} onChange={setPlafond} inputMode="numeric" style={{width: 160}}/>
                        </div>
                    )}

                    {vente && vente.discretionAutorisee !== false && (
                        <div className="ench-sheet-option">
                            <div>
                                <div className="ench-sheet-option-titre"><Trans>Enchérir discrètement</Trans></div>
                                <div className="ench-sheet-option-aide"><Trans>Les autres verront « Un fidèle » à la place de votre nom.</Trans></div>
                            </div>
                            <Switch checked={discret} onChange={setDiscret}/>
                        </div>
                    )}

                    <Button
                        type="primary"
                        size="large"
                        block
                        disabled={!montant || (avecPlafond && plafond && plafond < montant)}
                        onClick={() => setEtape("confirmation")}
                    >
                        <Trans>Continuer</Trans>
                    </Button>
                </>
            ) : (
                <>
                    {/* L'engagement, écrit en toutes lettres. C'est le seul écran
                        du parcours qui ralentit volontairement le fidèle. */}
                    <div className="ench-confirm">
                        <div className="ench-confirm-titre">
                            <Trans>Vous vous engagez à donner {formatMontant(montant, devise, lang)} si vous l'emportez.</Trans>
                        </div>
                        {avecPlafond && plafond > montant && (
                            <div className="ench-confirm-detail">
                                <Trans>Et jusqu'à {formatMontant(plafond, devise, lang)} si quelqu'un vous dépasse.</Trans>
                            </div>
                        )}
                        <div className="ench-confirm-detail">
                            <Trans>Une enchère ne peut pas être retirée.</Trans>
                        </div>
                    </div>

                    <Button type="primary" size="large" block loading={envoi} onClick={confirmer}>
                        <Trans>Je confirme mon enchère</Trans>
                    </Button>
                    <Button type="text" block onClick={() => setEtape("montant")}>
                        <Trans>Revenir</Trans>
                    </Button>
                </>
            )}
        </Drawer>
    )
}

// Les refus de l'API sont des codes ; le fidèle a besoin d'une phrase, et
// surtout de savoir quoi faire ensuite.
function messageErreur(res, devise, lang) {
    const code = res && res.error
    switch (code) {
        case "MONTANT_TROP_BAS":
            return res.minimum
                ? t`Quelqu'un vient de proposer davantage : il faut maintenant au moins ${formatMontant(res.minimum, devise, lang)}.`
                : t`Il faut proposer davantage.`
        case "LOT_TERMINE":
        case "LOT_FERME":
            return t`Ce lot vient de fermer.`
        case "PERIODE_CHOMEE":
            return t`Les enchères sont closes : Chabbat est entré.`
        case "LOT_SUR_PLACE":
            return t`Ce lot se vend à la synagogue.`
        case "TRIBU_INCOMPATIBLE":
            return t`Cet honneur est réservé.`
        case "TRIBU_INCONNUE":
            return t`Indiquez d'abord si vous êtes Cohen, Lévi ou Israël dans votre profil.`
        case "IDENTIFICATION_REQUISE":
            return t`Votre session a expiré : identifiez-vous à nouveau.`
        case "TOO_MANY_REQUESTS":
            return t`Trop d'enchères d'affilée : patientez quelques instants.`
        default:
            return t`Votre enchère n'est pas passée. Réessayez.`
    }
}
